import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import apiFetch from '@wordpress/api-fetch';

const StockCheckApp = () => {
	const [ region, setRegion ] = useState( 'All' );
	const [ partNumbers, setPartNumbers ] = useState( [ '' ] );
	const [ results, setResults ] = useState( null );
	const [ filteredResults, setFilteredResults ] = useState( null );
	const [ processing, setProcessing ] = useState( false );
	const firstInput = useRef( null );

	useEffect( () => {
		if ( ! results ) {
			setFilteredResults( null );
			return;
		}

		const nextFilteredResults = {};
		Object.keys( results ).forEach( ( partNumber ) => {
			const rows = [];
			results[ partNumber ].forEach( ( row ) => {
				if ( row.region === 'Global' || row.region === region || region === 'All' ) {
					rows.push( row );
				}
			} );
			if ( rows.length > 0 ) {
				nextFilteredResults[ partNumber ] = rows;
			}
		} );
		setFilteredResults( nextFilteredResults );
	}, [ results, region ] );

	const requestStock = ( query ) => {
		const path = `/component-selector/v1/stock?search=${ query }`;
		apiFetch( { path } )
			.then( ( response ) => {
				setResults( response );
				setProcessing( false );
			} )
			.catch( () => {
				setResults( {} );
				setProcessing( false );
			} );
	};

	const onSubmit = ( event ) => {
		event.preventDefault();
		setProcessing( true );
		if ( window.dataLayer && Array.isArray( window.dataLayer ) ) {
			window.dataLayer.push( { event: 'stock_check' } );
		}
		const query = partNumbers.filter( Boolean ).join( ',' );
		requestStock( query );
	};

	return (
		<div className="container">
			<div className="content">
				<div className="sidebar">
					<form onSubmit={ onSubmit }>
						<label>
							<span>Region</span>
							{ region !== 'All' && (
								<span className="reset" onClick={ () => setRegion( 'All' ) }>
									Reset
								</span>
							) }
						</label>
						<div className="select-wrapper">
							<select
								value={ region }
								onChange={ ( event ) => setRegion( event.target.value ) }
								tabIndex="-1"
							>
								<option>All</option>
								<option>Americas</option>
								<option>APAC</option>
								<option>EMEA</option>
							</select>
							<svg className="far fa-chevron-down"></svg>
						</div>
						<label>
							<span>Part numbers</span>
							{ ( partNumbers[ 0 ] || partNumbers.length > 1 ) && (
								<span
									className="reset"
									onClick={ () => {
										setPartNumbers( [ '' ] );
										if ( firstInput.current ) {
											firstInput.current.focus();
										}
									} }
								>
									Reset
								</span>
							) }
						</label>
						{ partNumbers.map( ( item, index ) => (
							<input
								ref={ index === 0 ? firstInput : null }
								key={ `part-number-${ index }` }
								autoFocus={ index === 0 }
								placeholder="e.g. PUB0312D2B"
								value={ partNumbers[ index ] }
								onChange={ ( event ) => {
									const newPartNumbers = [ ...partNumbers ];
									newPartNumbers[ index ] = event.target.value.replace( /\s/g, '' );
									setPartNumbers( newPartNumbers );
								} }
							/>
						) ) }
						{ partNumbers.length < 5 && (
							<div
								className="add-parts"
								onClick={ () => {
									const newPartNumbers = [ ...partNumbers, '' ];
									setPartNumbers( newPartNumbers );
								} }
							>
								Add more part numbers
							</div>
						) }
						<button type="submit">
							<span>Search</span>
						</button>
					</form>
					<div className="powered-by">
						<div className="text">Powered by</div>
						<div>
							<img
								height="24"
								src="/wp-content/plugins/component-selector/includes/blocks/stock-check/images/stock-check-logo-trusted-parts.png"
								alt="Trusted Parts"
							/>
						</div>
					</div>
				</div>
				<div className="results">
					{ processing && (
						<div className="status">
							<svg className="fad fa-spinner-third fa-spin"></svg>
						</div>
					) }
					{ ! processing && ! results && (
						<div className="status">
							<img
								height="36"
								src="/wp-content/plugins/component-selector/includes/blocks/stock-check/images/stock-check-logo-avnet.png"
								alt="Avnet"
							/>
							<img
								height="36"
								src="/wp-content/plugins/component-selector/includes/blocks/stock-check/images/stock-check-logo-digi-key-v2.png"
								alt="Digi-Key"
							/>
							<img
								height="60"
								src="/wp-content/plugins/component-selector/includes/blocks/stock-check/images/stock-check-logo-mouser.png"
								alt="Mouser"
							/>
							<img
								height="60"
								src="/wp-content/plugins/component-selector/includes/blocks/stock-check/images/stock-check-logo-tti.png"
								alt="TTI"
							/>
						</div>
					) }
					{ ! processing && results && filteredResults && Object.keys( filteredResults ).length < 1 && (
						<div className="status">No stock was found</div>
					) }
					{ ! processing && results && filteredResults && Object.keys( filteredResults ).length > 0 && (
						<div>
							<table cellPadding="0" cellSpacing="0">
								<thead>
									<tr>
										<th>Part number</th>
										<th>Quantity</th>
										<th>Region</th>
										<th>Distributor</th>
									</tr>
								</thead>
								<tbody>
									{ Object.keys( filteredResults )
										.sort()
										.map( ( partNumber ) => (
											<React.Fragment key={ partNumber }>
												{ filteredResults[ partNumber ].map( ( part, index ) => (
													<tr key={ `${ partNumber }-${ index }` }>
														{ index === 0 && (
															<td
																className="part-number"
																rowSpan={ filteredResults[ partNumber ].length }
															>
																{ partNumber }
															</td>
														) }
														<td>{ part.quantity }</td>
														<td>{ part.region }</td>
														<td>
															<a href={ part.url } target="_blank" rel="noreferrer">
																<span>{ part.distributor.replace( ' Electronics', '' ) }</span>
																<svg class="svg-inline--fa fa-external-link" data-prefix="far" data-icon="external-link" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" data-fa-i2svg=""><path fill="currentColor" d="M304 24c0 13.3 10.7 24 24 24l102.1 0L207 271c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l223-223L464 184c0 13.3 10.7 24 24 24s24-10.7 24-24l0-160c0-13.3-10.7-24-24-24L328 0c-13.3 0-24 10.7-24 24zM72 32C32.2 32 0 64.2 0 104L0 440c0 39.8 32.2 72 72 72l336 0c39.8 0 72-32.2 72-72l0-128c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 128c0 13.3-10.7 24-24 24L72 464c-13.3 0-24-10.7-24-24l0-336c0-13.3 10.7-24 24-24l128 0c13.3 0 24-10.7 24-24s-10.7-24-24-24L72 32z"></path></svg>
															</a>
														</td>
													</tr>
												) ) }
											</React.Fragment>
										) ) }
								</tbody>
							</table>
						</div>
					) }
				</div>
			</div>
		</div>
	);
};

const mountStockCheck = () => {
	const roots = document.querySelectorAll( '[data-stock-check-root]' );
	if ( ! roots.length ) {
		return;
	}

	roots.forEach( ( root ) => {
		createRoot( root ).render( <StockCheckApp /> );
	} );
};

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', mountStockCheck );
} else {
	mountStockCheck();
}
